import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { ApiError } from '../../api/client'
import {
  createRole,
  deleteRole,
  removeRoleModule,
  renameRole,
  roleQuery,
  rolesQuery,
  saveRoleDefaults,
  type UsersShell,
} from '../../api/settingsUsers'
import { DataTable, useTableState, type TableColumn } from '../../ui/DataTable'
import { Forbidden } from '../../ui/Forbidden'
import { Modal } from '../../ui/Modal'
import { Spinner } from '../../ui/Spinner'
import { toast } from '../../ui/notify'
import { initialPermissions, PermissionsGrid, type PermissionMap } from './PermissionsGrid'
import { RoleSettings } from './RoleSettings'
import { esc, failed, tableRequest, whenText } from './shared'
import './RolesAccess.css'

/**
 * Roles and Resources - `active/roles.php` (lista + doua modale) si
 * `active/roleDefaults.php` (drepturile implicite ale rolului, in randul
 * desfasurat).
 *
 * -----------------------------------------------------------------------------
 * DREPTURILE ROLULUI SE DESENEAZA CA ALE UNUI OM
 * -----------------------------------------------------------------------------
 *
 * `roleDefaults.php` era forma VECHE: fiecare modul un panou care se desface,
 * toate unul sub altul, cu patru radiouri pe rand si un buton "Save" propriu.
 * Ecranul de drepturi al unui OM a fost redesenat intre timp (coloana de module
 * la stanga, resursele la dreapta - vezi `permissions.php`), si aceleasi date
 * se arata acum la fel in amandoua locurile: e aceeasi intrebare pusa de doua
 * ori, o data despre un om si o data despre un rol.
 *
 * Ce s-a pastrat din forma veche: butonul "Remove module", care sterge dintr-o
 * data toate drepturile rolului pe modulul deschis (`removeRoleModule`).
 * Casutele "Remove" de pe fiecare resursa au disparut: ele faceau exact ce face
 * acum golirea randului (`×`), doar ca prin alt drum.
 *
 * -----------------------------------------------------------------------------
 * ROLURILE DE SISTEM
 * -----------------------------------------------------------------------------
 *
 * Un rol cu `sp` NULL e al instalarii, nu al furnizorului: lista nu-i deseneaza
 * butoanele (ca in `RoleModel`), iar capatul refuza si redenumirea, si
 * stergerea - in portal le primea pe amandoua.
 */

const ORDER_NAMES = ['', 'created_at', 'name', '']

function roleColumns(may: Record<string, boolean>): TableColumn[] {
  return [
    {
      index: 0,
      title: '',
      orderable: false,
      visible: true,
      width: 30,
      html: (row) =>
        `<center class="show_details color-dark-grey pointer font-16" data-id="${Number(row.id)}" data-type=""><i class="fas fa-plus-circle"></i></center>`,
    },
    {
      index: 1,
      title: 'Date',
      titleHtml: '<i class="far fa-calendar"></i> Date',
      orderable: true,
      visible: true,
      width: 150,
      className: 'td-small a-left',
      html: (row) => esc(whenText(row.created_at)),
    },
    {
      index: 2,
      title: 'Name',
      orderable: true,
      visible: true,
      className: 'td-small a-left',
      html: (row) => esc(row.name),
    },
    {
      index: 3,
      title: 'Actions',
      orderable: false,
      visible: true,
      html: (row) => {
        /* Rolurile de sistem (`sp` null) n-au butoane - ca in `RoleModel`. */
        if (row.sp === null || row.sp === undefined) return ''
        const id = Number(row.id)
        let html = ''
        if (may['settings.user_accounts.delete']) {
          html +=
            `<button class="btn btn-danger btn-xs pull-right od-delete-role" data-role="${id}">` +
            '<i class="fas fa-trash" aria-hidden="true"></i> Remove</button>'
        }
        if (may['settings.user_accounts.edit']) {
          html +=
            `<button class="btn btn-primary btn-xs pull-right od-edit-role" style="margin-right:5px;" data-role="${id}" data-role-name="${esc(row.name)}">` +
            '<i class="fas fa-pencil-alt" aria-hidden="true"></i> Edit</button>'
        }
        return html
      },
    },
  ]
}

export function RolesPanel({ shell }: { shell: UsersShell }) {
  const client = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<{ id: number; name: string } | null>(null)
  const [state, setState] = useTableState('settings-users-roles', {
    start: 0,
    length: 25,
    order: { column: 1, dir: 'desc' },
    search: '',
  })
  const columns = useMemo(() => roleColumns(shell.may), [shell.may])
  const table = useQuery({
    ...rolesQuery(tableRequest(state, ORDER_NAMES, 'created_at')),
    placeholderData: keepPreviousData,
  })

  if (table.error instanceof ApiError && table.error.status === 403) return <Forbidden />

  const rows = (table.data?.data ?? []) as unknown as Record<string, unknown>[]
  const reload = () => void client.invalidateQueries({ queryKey: ['settings', 'users', 'roles'] })

  const drop = (id: number) => {
    if (!confirm('Remove this role?')) return
    void deleteRole(id)
      .then(() => {
        toast('Role removed', 'Success')
        reload()
      })
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
  }

  return (
    <div id="content">
      {shell.may['settings.user_accounts.edit'] && (
        <button type="button" className="pull-right btn btn-success btn-sm" onClick={() => setAdding(true)}>
          <i className="fas fa-plus" aria-hidden="true"></i> New role
        </button>
      )}
      <h4>
        <strong>Roles</strong>
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage <strong>roles</strong>
      </label>
      <hr />

      <div
        onClick={(event) => {
          const target = event.target as HTMLElement
          const edit = target.closest('.od-edit-role')
          if (edit) {
            setEditing({
              id: Number(edit.getAttribute('data-role') ?? 0),
              name: edit.getAttribute('data-role-name') ?? '',
            })
            return
          }
          const remove = target.closest('.od-delete-role')
          if (remove) drop(Number(remove.getAttribute('data-role') ?? 0))
        }}
      >
        <DataTable
          id="settings-users-roles"
          columns={columns}
          rows={rows}
          total={table.data?.recordsFiltered ?? 0}
          fetching={table.isFetching}
          error={table.error ? failed(table.error) : null}
          onRetry={() => void table.refetch()}
          state={state}
          onState={setState}
          rowId={(row) => `rowd_${Number(row.id)}`}
          emptyHint="A role is the set of rights a person gets when the account is made."
          details={(row) => <RoleDefaults roleId={Number(row.id)} />}
        />
      </div>

      <RoleNameModal
        open={adding}
        title="New role"
        name=""
        onClose={() => setAdding(false)}
        onSubmit={(name) => createRole(name)}
        onSaved={() => {
          setAdding(false)
          reload()
        }}
      />

      <RoleNameModal
        open={editing !== null}
        title="Edit role"
        name={editing?.name ?? ''}
        onClose={() => setEditing(null)}
        onSubmit={(name) => renameRole(editing?.id ?? 0, name)}
        onSaved={() => {
          setEditing(null)
          reload()
        }}
      />
    </div>
  )
}

function RoleNameModal({
  open,
  title,
  name,
  onClose,
  onSubmit,
  onSaved,
}: {
  open: boolean
  title: string
  name: string
  onClose(): void
  onSubmit(name: string): Promise<unknown>
  onSaved(): void
}) {
  const [value, setValue] = useState(name)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (open) setValue(name)
  }, [open, name])

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    void onSubmit(value)
      .then(() => {
        toast('Role saved', 'Success')
        onSaved()
      })
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
      .finally(() => setBusy(false))
  }

  return (
    <Modal id="roleModal" open={open} onClose={onClose} title={title} labelledBy="roleModalLabel">
      <form className="form-horizontal" method="post" onSubmit={submit}>
        <div className="form-group">
          <label className="col-sm-3 control-label">
            Name <span className="required">*</span>
          </label>
          <div className="col-sm-9">
            <input
              type="text"
              className="form-control input-sm"
              required
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
          </div>
        </div>
        <div className="form-group">
          <div className="col-sm-offset-3 col-sm-9 aright">
            <button type="button" className="btn btn-default" data-dismiss="modal" onClick={onClose}>
              Close
            </button>{' '}
            <button type="submit" className="btn btn-primary" disabled={busy}>
              Save
            </button>
          </div>
        </div>
      </form>
    </Modal>
  )
}

/** Randul desfasurat al unui rol: drepturile lui implicite. */
export function RoleDefaults({ roleId }: { roleId: number }) {
  const client = useQueryClient()
  const page = useQuery(roleQuery(roleId))
  const [chosen, setChosen] = useState<PermissionMap>({})
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (page.data) setChosen(initialPermissions(page.data.resources))
  }, [page.data])

  if (page.error) return <div className="alert alert-danger">{failed(page.error)}</div>
  if (!page.data) return <Spinner />

  const reload = () => void client.invalidateQueries({ queryKey: ['settings', 'users', 'roles', roleId] })

  const submit = () => {
    setBusy(true)
    /*
     * Modulele fara nicio treapta bifata se trimit ca `remove`: capatul le
     * sterge randurile. Fara asta, un modul golit de tot n-ar avea nicio cheie
     * in `access`, deci capatul n-ar sti ca a fost atins si drepturile vechi ar
     * ramane pe loc.
     */
    const touched = new Set<number>()
    for (const key of Object.keys(chosen)) touched.add(Number(key.split('_')[0]))
    const emptied = page.data.modules.map((m) => m.id).filter((id) => !touched.has(id))

    void saveRoleDefaults(roleId, chosen, emptied)
      .then(() => {
        toast('Role was modified', 'Success')
        reload()
      })
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
      .finally(() => setBusy(false))
  }

  const dropModule = (moduleId: number) => {
    if (!confirm('Remove every right this role has on the module?')) return
    void removeRoleModule(roleId, moduleId)
      .then(() => {
        toast('Module removed from the role', 'Success')
        reload()
      })
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
  }

  /*
   * Doua coloane (19 octombrie 2026): drepturile, care sunt grosul, in coloana
   * lata din stanga; setarile rolului, un card ingust in dreapta, lipit sus cat
   * se defileaza prin grila. Sub 1200px se pun una sub alta, cardul intai - de
   * aceea sta primul si in DOM, iar pe ecran lat il muta grila CSS la dreapta.
   */
  return (
    <div className="od-role-layout" data-testid="role-layout">
      <aside className="od-role-layout-side" data-testid="role-layout-side">
        {/* Nivelul de acces si tichetele vazute (numai v4): un card, salvat separat de drepturi. */}
        <RoleSettings roleId={roleId} />
      </aside>
      <div className="od-role-layout-main" data-testid="role-layout-main">
        <PermissionsGrid
          modules={page.data.modules}
          resources={page.data.resources}
          chosen={chosen}
          onChange={setChosen}
          onSubmit={submit}
          saving={busy}
          saveLabel="Save role"
          extraActions={(moduleId) => (
            <button
              type="button"
              className="btn btn-danger btn-xs pull-right delete-role-module-btn"
              onClick={() => dropModule(moduleId)}
            >
              <i className="fas fa-trash" aria-hidden="true"></i> Remove module
            </button>
          )}
        />
      </div>
    </div>
  )
}
